import type { ApexOptions } from 'apexcharts'
import type { ChartSeriesItem } from '../types/chart.types'
import { ChartStyleColors } from './chart-colors'
import { compactNumberFormatter, localeNumberFormatter } from './chart-formatters'
import { createStandardTooltip } from './chart-tooltip'
import { buildDatetimeLineChartOptions, datetimeTooltipHeaderFormatter } from './line-chart.config'

export interface DualAxisLineChartConfig {
  dates: string[]
  leftSeries: ChartSeriesItem[]
  rightSeries: ChartSeriesItem
  rightFormatter: (v: number) => string
  leftFormatter?: (v: number) => string
}

export const buildDualAxisLineChartOptions = (config: DualAxisLineChartConfig): ApexOptions => {
  const { dates, leftSeries, rightSeries, rightFormatter, leftFormatter = compactNumberFormatter } = config

  // Right series is appended LAST — the yaxis array below relies on this order
  const allSeries = [...leftSeries, rightSeries]
  const rightSeriesIndex = leftSeries.length

  const baseOptions = buildDatetimeLineChartOptions({
    series: allSeries,
    dates,
    yAxisFormatter: leftFormatter
  })

  // ApexCharts multi-axis: when `yaxis` is an array it is matched to `series`
  // POSITIONALLY, so it must contain one entry per series, in series order.
  // All left-series entries reference the first left series via `seriesName`
  // so they share a single left scale (only the first one renders labels);
  // the final entry is the right series' own axis with `opposite: true`.
  const yaxis: ApexYAxis[] = [
    ...leftSeries.map((_, index) => ({
      seriesName: leftSeries[0].name,
      show: index === 0,
      labels: {
        style: { fontSize: '12px', colors: ChartStyleColors.labelColor },
        formatter: leftFormatter
      }
    })),
    {
      seriesName: rightSeries.name,
      opposite: true,
      labels: {
        style: { fontSize: '12px', colors: ChartStyleColors.labelColor },
        formatter: rightFormatter
      }
    }
  ]

  return {
    ...baseOptions,
    yaxis,
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      labels: { colors: ChartStyleColors.labelColor },
      markers: { size: 5, shape: 'square' as const },
      itemMargin: { horizontal: 8 }
    },
    tooltip: {
      ...baseOptions.tooltip,
      // Per-series formatting: the right series uses its own formatter
      custom: createStandardTooltip({
        valueFormatter: localeNumberFormatter,
        headerFormatter: datetimeTooltipHeaderFormatter,
        seriesValueFormatter: (value, seriesIndex) =>
          seriesIndex === rightSeriesIndex ? rightFormatter(value) : localeNumberFormatter(value)
      })
    }
  }
}
